import 'react-datepicker/dist/react-datepicker.css';

import React, {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import DatePicker, {
  ReactDatePickerCustomHeaderProps,
  ReactDatePickerProps,
} from 'react-datepicker';
import { Control, Controller, Path } from 'react-hook-form';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { NOOP } from '@lootboy/lib/utils';
import { range } from '@lootboy/lib/utils/arrays';
import { getDateFormatUnicode } from '@lootboy/lib/utils/dateAndTime';

import { Divider } from '~/components/atoms/Divider';
import { Expandable } from '~/components/atoms/Expandable';
import Input from '~/components/atoms/FormControls/Input';
import { IInputWrapperProps } from '~/components/atoms/FormControls/InputWrapper';
import ChevronLeftIcon from '~/components/atoms/Icons/ChevronLeftIcon';
import ChevronTurningPlainIcon from '~/components/atoms/Icons/ChevronTurningPlainIcon';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import ButtonClean from '~/components/ButtonClean';

import useBooleanState from '~/hooks/useBooleanState';
import { useOnClickOutsideWithRef } from '~/hooks/useOnClickOutside';
import { languageSelector } from '~/redux/selectors';

import { ReactComponent as ChevronIcon } from '~/assets/images/iconsLibrary/chevron-left.svg';

import styles from './DateInput.module.scss';

type TDateInputProps<FormValues> = IInputWrapperProps & {
  control: Control<FormValues>;
  name: Path<FormValues>;
  id: string;
  placeholder: string;
  datePickerProps?: Partial<ReactDatePickerProps>;
};

const MIN_YEAR = 1900;
const MONTHS = range(12);

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = range(CURRENT_YEAR - MIN_YEAR).map((i) => CURRENT_YEAR - i);

function DateInputHeaderRenderer(
  {
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    nextMonthButtonDisabled,
    prevMonthButtonDisabled,
  }: ReactDatePickerCustomHeaderProps,
  ref: Ref<{ isPickerOpened: () => boolean }>,
): JSX.Element {
  const [isPickerOpened, togglePickerOpened] = useBooleanState();
  const { setFalse: closePicker } = togglePickerOpened;
  const selectedMonth = date.getMonth();
  const selectedYear = date.getFullYear();

  useImperativeHandle(ref, () => ({ isPickerOpened: () => isPickerOpened }), [
    isPickerOpened,
  ]);

  // tracks if month/year has been manually selected
  const [monthSelected, setMonthSelected] = useState(false);
  const [yearSelected, setYearSelected] = useState(false);

  // reset trackers when month/year picker closes
  useEffect(() => {
    if (!isPickerOpened) {
      setMonthSelected(false);
      setYearSelected(false);
    }
  }, [isPickerOpened]);

  // if both month and year have been selected close the picker
  useEffect(() => {
    if (isPickerOpened && monthSelected && yearSelected) {
      closePicker();
    }
  }, [closePicker, isPickerOpened, monthSelected, yearSelected]);

  return (
    <div className={styles.pickerHeader}>
      <ButtonClean
        onClick={togglePickerOpened}
        className={styles.currentDate}
      >
        {format(date, 'MMM yyyy')}
        <ChevronIcon
          className={classNames(
            styles.currentDateIcon,
            isPickerOpened && styles.iconOpened,
          )}
        />
      </ButtonClean>
      {!isPickerOpened && (
        <div className={styles.monthNavigation}>
          <ChevronLeftIcon
            onClick={decreaseMonth}
            background={false}
            className={classNames(styles.navigationIcon, styles.prev)}
            disabled={prevMonthButtonDisabled}
          />
          <ChevronLeftIcon
            onClick={increaseMonth}
            background={false}
            className={classNames(styles.navigationIcon, styles.next)}
            disabled={nextMonthButtonDisabled}
          />
        </div>
      )}
      {isPickerOpened && (
        <div className={styles.rangePicker}>
          <div className={styles.monthsContainer}>
            {MONTHS.map((i) => (
              <ButtonClean
                className={classNames(
                  styles.month,
                  i === selectedMonth && styles.selectedMonth,
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setMonthSelected(true);
                  changeMonth(i);
                }}
                key={i}
              >
                {format(new Date().setMonth(i), 'MMM')}
              </ButtonClean>
            ))}
          </div>
          <Divider className={styles.divider} />
          <ScrollContainer flex>
            <div className={styles.yearsContainer}>
              {YEARS.map((year) => (
                <ButtonClean
                  className={classNames(
                    styles.year,
                    year === selectedYear && styles.selectedYear,
                  )}
                  key={year}
                  onClick={(e) => {
                    e.stopPropagation();
                    setYearSelected(true);
                    changeYear(year);
                  }}
                >
                  {year}
                </ButtonClean>
              ))}
            </div>
          </ScrollContainer>
        </div>
      )}
    </div>
  );
}

const DateInputHeader = forwardRef(DateInputHeaderRenderer);

const ID = uuidv4();

export function DateInput<FormValues>({
  control,
  name,
  id,
  label,
  error,
  placeholder,
  datePickerProps,
  errorAction,
}: TDateInputProps<FormValues>): React.ReactElement {
  const language = useSelector(languageSelector);
  const [isCalendarOpened, changeIsCalendarOpened] = useBooleanState();

  const calendarHeaderHandle = useRef<{ isPickerOpened: () => boolean }>(null);

  const { setFalse: setCalendarClosed } = changeIsCalendarOpened;
  const [setRef] = useOnClickOutsideWithRef<HTMLDivElement>(setCalendarClosed);

  return (
    <div ref={setRef}>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, onBlur } }) => (
          <>
            <DatePicker
              popperClassName={styles.lootBoyDatePicker}
              enableTabLoop={false}
              dateFormat={getDateFormatUnicode(language)}
              id={id}
              customInput={
                <Input
                  label={label}
                  errorAction={errorAction}
                  error={error}
                  id={id}
                  type="text"
                  onIconClick={NOOP} // to make cursor a pointer
                  onClickWrapper={() => {
                    if (isCalendarOpened) {
                      document.getElementById(id)?.blur();
                    }
                    changeIsCalendarOpened();
                  }}
                  icon={<ChevronTurningPlainIcon rotated={isCalendarOpened} />}
                />
              }
              onBlur={onBlur}
              adjustDateOnChange
              renderCustomHeader={(props) => (
                <DateInputHeader
                  {...props}
                  ref={calendarHeaderHandle}
                />
              )}
              onChange={(...args) => {
                onChange(...args);
                if (!calendarHeaderHandle.current?.isPickerOpened()) {
                  setCalendarClosed();
                }
              }}
              selected={value as Date}
              placeholderText={placeholder}
              showPopperArrow={false}
              portalId={ID}
              open
              fixedHeight
              {...datePickerProps}
            />
            <Expandable
              isExpanded={isCalendarOpened}
              id={ID}
            />
          </>
        )}
      />
    </div>
  );
}
